import {
  Alert,
  Box,
  Button,
  Flex,
  HStack,
  IconButton,
  Spinner,
  Tag,
  Text,
  Tooltip,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import RefreshSharpIcon from '@gamma/ui/icons/RefreshSharpIcon';
import { MsDefenderMark } from '@gamma/ui/logos';
import { Modal } from '@gamma/ui/overlay';
import { GraphQLReqStatus } from '@gamma/ui/progress';
import moment from 'moment';
import { useContext, useMemo, useState } from 'react';
import { i18n } from '../../../../shared/localization';
import { AuthContext } from '../../../../shared/providers/AuthProvider';
import {
  EntityEdrStatusWithTimestamp,
  MicrosoftDefenderEndpointDevice,
  useGetEntityEdrStatus,
  usePerformEdrAction,
  usePullEntityEdrStatus,
} from '../../../entities/queries';
import { Detection } from '../../../../shared/generated/graphql';
import { EntityPanelRow } from './Panels';
import { usePendingEdrStateTooltip } from '../../hooks';

const { entityEDR } = i18n.pages.detections;
const { edrAction } = entityEDR;
export type MSDefenderDetectionDataPanelProps = {
  data: MicrosoftDefenderEndpointDevice;
  detectionInfo: Detection;
  isRelated?: boolean;
  disableBorders?: boolean;
  actionEnabled: boolean;
};

export const MSDefenderDetectionDataPanel = ({
  data,
  detectionInfo,
  actionEnabled,
}: MSDefenderDetectionDataPanelProps) => {
  const [edrStatus, setEdrStatus] = useState<
    EntityEdrStatusWithTimestamp | undefined
  >();
  const { userRole } = useContext(AuthContext);

  const showToastMessage = useToast();

  const {
    isOpen: edrActionIsOpen,
    onOpen: edrActionOnOpen,
    onClose: edrActionOnClose,
  } = useDisclosure();

  const { alert_entity, tenant_info } = detectionInfo || {};
  const { entity_name } = alert_entity || {};
  const { tenant_id } = tenant_info || {};

  const { machine, entity_id, last_updated_timestamp } = data;

  const {
    osPlatform,
    version,
    osProcessor,
    ipAddresses,
    lastExternalIpAddress,
  } = machine || {};

  const mac_address = ipAddresses?.find(
    (ip) => ip.ipAddress === entity_name,
  )?.macAddress;
  const { entity_edr_status } = edrStatus || {};

  const entityEdrIsolated =
    entity_edr_status === 'isolated' ||
    entity_edr_status === 'lift_isolation_pending';

  const edrStatusAlert = useMemo(() => {
    if (entity_edr_status) {
      switch (entity_edr_status) {
        case 'normal':
          return {
            colorScheme: 'green',
            statusText: edrAction.normal,
          };
        case 'isolated':
          return {
            colorScheme: 'red',
            statusText: edrAction.isolated,
          };
        case 'isolation_pending':
          return {
            colorScheme: 'yellow',
            statusText: edrAction.pendingIsolation,
          };
        case 'lift_isolation_pending':
          return {
            colorScheme: 'yellow',
            statusText: edrAction.pendingLift,
          };
      }
    }
    return undefined;
  }, [entity_edr_status]);

  const pendingStateTooltip = usePendingEdrStateTooltip(entity_edr_status);

  const { loading: getEdrStatusLoading, error: getEdrStatusError } =
    useGetEntityEdrStatus({
      skip: !entity_id || !tenant_id,
      fetchPolicy: 'network-only',
      variables: {
        entity_id,
        tenant: tenant_id ?? '',
      },
      onCompleted({ getEntityEdrStatus }) {
        const { entity_edr_status, tenant, updated_timestamp } =
          getEntityEdrStatus;
        setEdrStatus({
          tenant,
          entity_edr_status,
          updated_timestamp,
        });
      },
    });

  const [pullEntityEdrStatus, { loading: pullEdrStatusLoading }] =
    usePullEntityEdrStatus({
      variables: {
        entity_id,
        tenant: tenant_id ?? '',
      },
      onCompleted({ pullEntityEdrStatus }) {
        const { tenant, entity_edr_status, updated_timestamp } =
          pullEntityEdrStatus;
        setEdrStatus({
          tenant,
          entity_edr_status,
          updated_timestamp,
        });
        showToastMessage({
          status: 'success',
          title: edrAction.toasts.success,
          description: edrAction.toasts.refreshSuccessful,
          isClosable: true,
          position: 'bottom-right',
        });
      },
      onError: () => {
        showToastMessage({
          status: 'error',
          title: edrAction.toasts.error,
          description: edrAction.toasts.refreshFailed,
          isClosable: true,
          position: 'bottom-right',
        });
      },
    });

  const [performEdrAction, { loading: performEdrActionLoading }] =
    usePerformEdrAction({
      variables: {
        tenant: tenant_id ?? '',
        action: !entityEdrIsolated ? 'isolate' : 'lift_isolation',
        edr_device_input: {
          entity_id,
          detection_id: detectionInfo?.detection_id,
        },
      },
      onCompleted(data) {
        edrActionOnClose();
        showToastMessage({
          status: 'success',
          title: edrAction.toasts.success,
          description: edrAction.toasts.requestSubmitted,
          isClosable: true,
          position: 'bottom-right',
        });
        setEdrStatus({
          tenant: data?.performEdrAction?.tenant,
          entity_edr_status: data?.performEdrAction?.entity_edr_status,
          updated_timestamp: data?.performEdrAction?.updated_timestamp,
        });
      },
      onError: () => {
        showToastMessage({
          status: 'error',
          title: edrAction.toasts.error,
          description: edrAction.toasts.requestFailed,
          isClosable: true,
          position: 'bottom-right',
        });
      },
    });

  const handleEdrActionEvent = () => {
    if (entity_id && tenant_id && detectionInfo?.detection_id) {
      performEdrAction();
    }
  };
  return (
    <>
      <HStack>
        <MsDefenderMark />
        <Text textStyle="h5">Microsoft Defender</Text>
      </HStack>
      {actionEnabled &&
        (getEdrStatusError || getEdrStatusLoading ? (
          <GraphQLReqStatus
            loading={getEdrStatusLoading}
            error={getEdrStatusError}
          />
        ) : (
          <Flex w="100%" data-testid="entity-edr-status">
            <EntityPanelRow heading={edrAction.entityStatus}>
              <HStack justifyContent="space-between">
                <HStack>
                  {edrStatusAlert && (
                    <Tooltip
                      label={pendingStateTooltip}
                      placement="top"
                      hasArrow
                    >
                      <Tag
                        size="md"
                        variant="solid"
                        colorScheme={edrStatusAlert?.colorScheme}
                      >
                        {edrStatusAlert?.statusText}
                      </Tag>
                    </Tooltip>
                  )}
                  <IconButton
                    aria-label={edrAction.refreshEntityStatus}
                    onClick={() => pullEntityEdrStatus()}
                    isDisabled={
                      getEdrStatusLoading ||
                      pullEdrStatusLoading ||
                      userRole === 'viewer'
                    }
                    icon={
                      getEdrStatusLoading || pullEdrStatusLoading ? (
                        <Box w="20px" h="20px">
                          <Spinner size="xs" color="white" />
                        </Box>
                      ) : (
                        <RefreshSharpIcon />
                      )
                    }
                  />
                </HStack>
                <Tooltip
                  w="120px"
                  placement="top"
                  label={
                    userRole !== 'admin'
                      ? edrAction.requestPermissions
                      : undefined
                  }
                >
                  <Button
                    size="xs"
                    variant="solid"
                    colorScheme="gray"
                    onClick={edrActionOnOpen}
                    isDisabled={
                      userRole !== 'admin' ||
                      getEdrStatusLoading ||
                      pullEdrStatusLoading ||
                      entity_edr_status === 'isolation_pending' ||
                      entity_edr_status === 'lift_isolation_pending'
                    }
                  >
                    {entityEdrIsolated
                      ? edrAction.liftIsolation
                      : edrAction.isolateEntity}
                  </Button>
                </Tooltip>
              </HStack>
            </EntityPanelRow>
          </Flex>
        ))}

      <EntityPanelRow heading={entityEDR.timestamp}>
        {last_updated_timestamp &&
          moment.unix(last_updated_timestamp).format('MMMM D, YYYY h:mma')}
      </EntityPanelRow>
      <EntityPanelRow heading={entityEDR.macAddress}>
        {mac_address}
      </EntityPanelRow>
      <EntityPanelRow heading={entityEDR.platform}>{osPlatform}</EntityPanelRow>
      <EntityPanelRow heading={entityEDR.osVersion}>{version}</EntityPanelRow>
      <EntityPanelRow heading={entityEDR.osProcessor}>
        {osProcessor}
      </EntityPanelRow>
      <EntityPanelRow heading={entityEDR.externalIp}>
        {lastExternalIpAddress}
      </EntityPanelRow>
      <Modal
        isOpen={edrActionIsOpen}
        onClose={edrActionOnClose}
        title={`${
          entityEdrIsolated
            ? edrAction.liftEntityIsolation
            : edrAction.isolateEntity
        } in Microsoft Defender`}
        body={
          entityEdrIsolated ? (
            <Text>
              <Text as="span" textStyle="body-md-bold">
                {entity_name}
              </Text>{' '}
              {edrAction.willBeAdded}
            </Text>
          ) : (
            <Alert variant="subtle" status="warning">
              <Text>
                <Text as="span" textStyle="body-md-bold">
                  {entity_name}
                </Text>{' '}
                {edrAction.willBeIsolated}
              </Text>
            </Alert>
          )
        }
        footer={
          <HStack>
            <Button
              variant="solid"
              colorScheme="gray"
              onClick={edrActionOnClose}
            >
              {edrAction.cancel}
            </Button>
            <Button
              variant="solid"
              colorScheme="blue"
              onClick={handleEdrActionEvent}
              isLoading={performEdrActionLoading}
              isDisabled={performEdrActionLoading}
            >
              {entity_edr_status === 'normal' ||
              entity_edr_status === 'isolation_pending'
                ? edrAction.isolateEntity
                : edrAction.liftIsolation}
            </Button>
          </HStack>
        }
      />
    </>
  );
};
